/**
 * 로카로라 크루 — 웹 → 앱 클릭 핸드오프의 **순수** 규약(클라이언트·서버 공용).
 *
 * 크루 링크 클릭은 그 브라우저의 la_ref 쿠키에 산다. 방문자가 앱을 설치해 로그인·결제하면
 * 앱 WebView 는 다른 쿠키 저장소라 그 클릭을 모른다. 그 틈을 사용자 단계 없이 메우는
 * 경로가 셋이다:
 *   ① 설치 페이지(/{locale}/app?c=&next=) — 스토어 버튼 두 개. 열리는 곳(브라우저·앱 안)에서
 *      c 를 채택(adoptCrewClick)하고, 앱 안이면 next 로 이어간다. iOS 의 App Store 버튼은
 *      서버 홉(/api/crew/app-store?c=&next=)을 거친다 — 그 라우트가 la_ref·la_next 를
 *      리다이렉트 응답에 심고 App Store 로 302 한다. iOS 인앱 브라우저에서는 그 홉을 Safari
 *      로 연다(Safari 저장소에 클릭을 남겨야 앱 로그인 창이 본다).
 *   ② Android Install Referrer — Play 스토어 URL 의 referrer 에 c·next 를 실어 설치 후
 *      첫 실행에서 회수한다.
 *   ③ 앱 소셜 로그인 핸드오프 — 로그인 창(시스템 브라우저 쿠키 저장소)이 본 la_ref 를
 *      nonce 아래 잠시 맡겨 두고 로그인 직후 앱이 회수한다(/api/crew/auth-handoff).
 *
 * 이 파일은 node 의존성이 없어야 한다(클라이언트 번들에 들어간다). URL 조합·UA 판정·
 * 경로 정제처럼 테스트로 고정할 수 있는 것만 둔다.
 */

import { LOCALE_PATTERN, locales } from "@/i18n/routing";
import { isCrewClickId } from "./click-id";

// ---------------------------------------------------------------- 스토어·앱 식별자

export const IOS_APP_ID = "6768569347";
export const APP_STORE_URL = `https://apps.apple.com/app/id${IOS_APP_ID}`;
export const ANDROID_PACKAGE = "com.locarora.app";
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/** 설치 페이지 경로 세그먼트(/{locale}/app). */
export const APP_INSTALL_SEGMENT = "app";

/** App Store 서버 홉(라우트 핸들러 app/api/crew/app-store). */
export const CREW_APP_STORE_HOP_PATH = "/api/crew/app-store";

// ---------------------------------------------------------------- 쿠키·수명

/**
 * 설치 페이지가 남기는 "이어갈 경로". 앱 로그인 핸드오프가 이 값을 함께 맡겨, 로그인 직후
 * 앱이 그 상품으로 바로 돌아가게 한다. 짧게 산다 — 하루 지난 "이어가기"는 의도가 아니다.
 */
export const CREW_NEXT_COOKIE = "la_next";
export const CREW_NEXT_MAX_AGE_SECONDS = 24 * 60 * 60;

/** 로그인 핸드오프 행의 수명. 로그인 한 번(소셜 동의 화면 포함)을 넉넉히 덮는다. */
export const CREW_HANDOFF_TTL_SECONDS = 15 * 60;

/** DB CHECK(crew_click_handoffs.next_path ≤ 512)와 같은 상한. */
export const CREW_NEXT_PATH_MAX_LENGTH = 512;

// ---------------------------------------------------------------- 경로

const LOCALE_SET = new Set<string>(locales);
const LOCALE_PREFIX = new RegExp(`^/(${LOCALE_PATTERN})(?=/|\\?|#|$)`);
const LOCALE_HOME_RE = new RegExp(`^/(${LOCALE_PATTERN})/?$`);

function pathnameOf(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

/**
 * 외부(쿼리·쿠키·리퍼러)에서 온 next 를 **같은 출처의 로케일 접두 내부 경로**로 정제한다.
 * 통과 못 하면 null — 호출자는 홈으로 떨어진다.
 *
 * - "/" 로 시작, "//"·역슬래시 금지(브라우저가 "/\evil.com" 을 "//evil.com" 으로 읽는다).
 *   디코드한 형태도 같은 검사를 받는다("/%2F%2Fevil.com" 류).
 * - 공백·제어문자 금지, 길이 ≤ 512(DB CHECK 와 동일).
 * - 로케일 접두가 없으면 fallbackLocale 로 붙인다(없으면 거절).
 * - 설치 페이지 자신은 거절한다 — next 가 /app 이면 앱 안에서 무한히 자기 자신으로 간다.
 */
export function sanitizeCrewNextPath(
  raw: unknown,
  fallbackLocale?: string | null,
): string | null {
  if (typeof raw !== "string") return null;
  let path = raw.trim();
  if (!path || path.length > CREW_NEXT_PATH_MAX_LENGTH) return null;
  if (!isSafeRelativePath(path)) return null;

  // 디코드 형태로도 한 번 더 본다. 값 자체는 디코드하지 않는다(쿼리의 %2F 등을 보존).
  let decoded = path;
  for (let i = 0; i < 3; i += 1) {
    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return null;
    }
    if (next === decoded) break;
    decoded = next;
  }
  if (!isSafeRelativePath(decoded)) return null;

  if (!LOCALE_PREFIX.test(path)) {
    if (!fallbackLocale || !LOCALE_SET.has(fallbackLocale)) return null;
    path = path === "/" ? `/${fallbackLocale}` : `/${fallbackLocale}${path}`;
    if (path.length > CREW_NEXT_PATH_MAX_LENGTH) return null;
  }

  if (isAppInstallPath(path)) return null;
  return path;
}

function isSafeRelativePath(path: string): boolean {
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("\\")) return false;
  // 공백·제어문자(탭/개행 포함) — 헤더 주입·URL 파싱 차이를 원천 차단.
  if (/[\s\u0000-\u001f\u007f]/.test(path)) return false;
  return true;
}

/** "/{locale}/app" (뒤에 / ? # 허용) 인가. */
export function isAppInstallPath(path: string): boolean {
  return new RegExp(
    `^/(${LOCALE_PATTERN})/${APP_INSTALL_SEGMENT}/?(?=\\?|#|$)`,
  ).test(path);
}

/** "/ko/products/x?y" → "/products/x" (로케일·쿼리 제거). 로케일이 없으면 pathname 그대로. */
export function stripLocalePrefix(path: string): string {
  const pathname = pathnameOf(path);
  const stripped = pathname.replace(LOCALE_PREFIX, "");
  return stripped === "" ? "/" : stripped;
}

/** 로그인 next 가 "홈"인가 — 홈일 때만 핸드오프의 next 가 이긴다(진행 중 흐름을 존중). */
export function isHomePath(path: string | null | undefined): boolean {
  if (!path) return true;
  const pathname = pathnameOf(path);
  return pathname === "/" || LOCALE_HOME_RE.test(pathname);
}

function crewQuery(input: { clickId?: string | null; next?: string | null }): string {
  const params = new URLSearchParams();
  if (input.clickId && isCrewClickId(input.clickId)) params.set("c", input.clickId);
  if (input.next) params.set("next", input.next);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 설치 페이지 상대 경로. 값이 없는 파라미터는 싣지 않는다. */
export function buildAppInstallPath(input: {
  locale: string;
  clickId?: string | null;
  next?: string | null;
}): string {
  const locale = LOCALE_SET.has(input.locale) ? input.locale : "ko";
  return `/${locale}/${APP_INSTALL_SEGMENT}${crewQuery(input)}`;
}

/**
 * App Store 서버 홉 상대 경로(/api/crew/app-store?c=&next=). 라우트가 클릭을 검증해
 * la_ref·la_next 를 리다이렉트 응답에 심고 App Store 로 302 한다 — 페이지의 JS 타이머가
 * 채택 완료를 기다리는 대신, 쿠키가 심긴 **같은 응답**이 App Store 로 보낸다.
 */
export function buildAppStoreHopPath(input: {
  clickId?: string | null;
  next?: string | null;
}): string {
  return `${CREW_APP_STORE_HOP_PATH}${crewQuery(input)}`;
}

// ---------------------------------------------------------------- 크루 링크 진입

/**
 * "웹에서 계속 보기"를 누른 방문자 표식. 있으면 크루 링크가 설치 페이지를 건너뛰고
 * 상품으로 바로 간다(같은 사람에게 매번 설치를 권하지 않는다). 클라이언트에서 쓰므로
 * httpOnly 가 아니다 — 값은 "1" 뿐이고 귀속과 무관하다.
 */
export const APP_INSTALL_SKIP_COOKIE = "la_app_skip";
export const APP_INSTALL_SKIP_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

/**
 * 크루 링크(/r/{code})가 설치 페이지를 먼저 보여줄지.
 *
 * 설치 페이지는 **크루 링크로 들어온 휴대폰 방문자에게만** 나온다(10-09 사용자 결정).
 *  - 휴대폰(iOS·Android) 브라우저일 것 — PC 는 상품으로 바로 간다.
 *  - 이번 클릭이 실제로 기록됐을 것(clickId) — 넘겨줄 클릭이 없으면 설치 페이지의 의미가 없다.
 *  - "웹에서 계속 보기"로 건너뛴 적이 없을 것(7일).
 * 앱 WebView 에서 열린 경우는 설치 페이지가 스스로 next 로 넘긴다(UA 로는 구분하지 않는다).
 */
export function shouldRouteCrewLinkToAppInstall(input: {
  userAgent: string | null | undefined;
  hasClick: boolean;
  skipCookie: string | null | undefined;
}): boolean {
  if (!input.hasClick) return false;
  if (input.skipCookie) return false;
  const ua = input.userAgent ?? "";
  return /iPhone|iPod|Android/i.test(ua) || (/iPad/i.test(ua) && /Mobile/i.test(ua));
}

// ---------------------------------------------------------------- 환경 판정

/**
 * iOS 인앱 브라우저 식별. Safari 쿠키 저장소가 아니므로(=앱 로그인 창이 그 클릭을 못
 * 본다) Safari 로 다시 열어야 한다.
 * - "other-browser": Chrome/Firefox/Edge iOS — 진짜 브라우저지만 Safari 저장소가 아니다.
 * - "webview": 알 수 없는 앱의 WKWebView(UA 에 Safari/ 토큰이 없다).
 */
export type IosInAppApp =
  | "instagram"
  | "facebook"
  | "tiktok"
  | "threads"
  | "kakaotalk"
  | "naver"
  | "line"
  | "twitter"
  | "daum"
  | "other-browser"
  | "webview";

export type AppHandoffEnv =
  | { kind: "native" }
  | { kind: "ios-inapp"; app: IosInAppApp }
  | { kind: "ios-safari" }
  | { kind: "android"; inApp: boolean }
  | { kind: "desktop" };

export function detectIosInAppApp(ua: string): IosInAppApp | null {
  if (/KAKAOTALK/i.test(ua)) return "kakaotalk";
  if (/\bLine\//i.test(ua)) return "line";
  if (/NAVER\(inapp/i.test(ua)) return "naver";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return "facebook";
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return "tiktok";
  if (/Barcelona|Threads/i.test(ua)) return "threads";
  if (/Twitter/i.test(ua)) return "twitter";
  if (/DaumApps/i.test(ua)) return "daum";
  if (/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|YaBrowser|DuckDuckGo|GSA\//i.test(ua)) {
    return "other-browser";
  }
  if (!/Safari\//i.test(ua)) return "webview";
  return null;
}

function isAndroidInApp(ua: string): boolean {
  return (
    /KAKAOTALK|\bLine\/|NAVER\(inapp|Instagram|FBAN|FBAV|FB_IAB|musical_ly|BytedanceWebview|TikTok|Barcelona|Threads|Twitter|DaumApps/i.test(
      ua,
    ) || /; wv\)/i.test(ua)
  );
}

export function detectAppHandoffEnv(input: {
  userAgent: string;
  isNative: boolean;
  /** navigator.maxTouchPoints — iPadOS 는 데스크톱 Safari UA(Macintosh)를 보낸다. */
  maxTouchPoints?: number;
}): AppHandoffEnv {
  if (input.isNative) return { kind: "native" };
  const ua = input.userAgent || "";
  const isIos =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && (input.maxTouchPoints ?? 0) > 1);
  if (isIos) {
    const app = detectIosInAppApp(ua);
    return app ? { kind: "ios-inapp", app } : { kind: "ios-safari" };
  }
  if (/Android/i.test(ua)) return { kind: "android", inApp: isAndroidInApp(ua) };
  return { kind: "desktop" };
}

// ---------------------------------------------------------------- URL 빌더

/**
 * 주어진 우리 쪽 URL(App Store 홉)을 iOS Safari 로 다시 여는 URL.
 * - KakaoTalk: 공식 openExternal 스킴.
 * - LINE: openExternalBrowser=1 쿼리(LINE 이 시스템 브라우저로 넘긴다).
 * - 그 외: x-safari-https:// — Safari 를 직접 여는 iOS 스킴. 인앱 브라우저가 막으면
 *   무반응이므로 호출자는 잠시 뒤에도 페이지가 보이면 App Store 로 바로 보낸다.
 */
export function buildSafariReopenUrl(pageUrl: string, app: IosInAppApp): string {
  if (app === "kakaotalk") {
    return `kakaotalk://web/openExternal?url=${encodeURIComponent(pageUrl)}`;
  }
  if (app === "line") {
    try {
      const url = new URL(pageUrl);
      url.searchParams.set("openExternalBrowser", "1");
      return url.toString();
    } catch {
      return pageUrl;
    }
  }
  return `x-safari-${pageUrl}`;
}

/** Install Referrer 로 실을 문자열("c=..&next=.."). 실을 것이 없으면 null. */
export function buildInstallReferrer(input: {
  clickId?: string | null;
  next?: string | null;
}): string | null {
  const params = new URLSearchParams();
  if (input.clickId && isCrewClickId(input.clickId)) params.set("c", input.clickId);
  if (input.next) params.set("next", input.next);
  const value = params.toString();
  return value || null;
}

/** Play 스토어 URL. referrer 는 한 번 더 인코딩해 한 파라미터로 싣는다(Play 규약). */
export function buildPlayStoreUrl(input: {
  clickId?: string | null;
  next?: string | null;
}): string {
  const referrer = buildInstallReferrer(input);
  return referrer
    ? `${PLAY_STORE_URL}&referrer=${encodeURIComponent(referrer)}`
    : PLAY_STORE_URL;
}

// ---------------------------------------------------------------- 스토어 버튼

export type StoreKind = "appstore" | "play";

/** 지금 기기의 스토어를 앞에. 판정 전(SSR·하이드레이션)과 iOS·데스크톱은 App Store 먼저. */
export function orderStoreButtons(env: AppHandoffEnv | null): StoreKind[] {
  return env?.kind === "android" ? ["play", "appstore"] : ["appstore", "play"];
}

/**
 * App Store 버튼의 href. iOS(Safari·인앱)이고 넘길 것(c 또는 next)이 있으면 서버 홉 —
 * 그 응답이 la_ref·la_next 를 심은 채 App Store 로 보내므로, 진입 시 채택이 아직 안 끝났어도
 * 쿠키가 보장된다. 그 외(Android·데스크톱·판정 전·넘길 것 없음)는 App Store 그대로.
 */
export function appStoreHrefFor(
  env: AppHandoffEnv | null,
  input: { clickId?: string | null; next?: string | null },
): string {
  if (env?.kind !== "ios-safari" && env?.kind !== "ios-inapp") return APP_STORE_URL;
  const hop = buildAppStoreHopPath(input);
  return hop === CREW_APP_STORE_HOP_PATH ? APP_STORE_URL : hop;
}

export type AppStoreTap =
  /** 링크(href) 그대로. */
  | { kind: "direct" }
  /** 기본 이동을 막고 홉을 Safari 로 연다(url). 무반응이면 fallbackUrl 로. */
  | { kind: "reopen"; url: string; fallbackUrl: string };

/**
 * App Store 버튼을 눌렀을 때. iOS 인앱 브라우저만 Safari 재오픈 — 앱 로그인 창은 Safari
 * 저장소를 보므로 클릭을 거기에도 남겨야 설치 뒤 이어진다. hopUrl 은 App Store 홉의
 * **절대** URL(appStoreHrefFor 가 홉을 돌려준 경우). 홉이 아니면(넘길 것이 없다) 재오픈할
 * 이유가 없다 — 그대로 App Store.
 */
export function decideAppStoreTap(env: AppHandoffEnv | null, hopUrl: string): AppStoreTap {
  if (env?.kind !== "ios-inapp") return { kind: "direct" };
  let isHop = false;
  try {
    isHop = new URL(hopUrl).pathname === CREW_APP_STORE_HOP_PATH;
  } catch {
    isHop = false;
  }
  if (!isHop) return { kind: "direct" };
  return { kind: "reopen", url: buildSafariReopenUrl(hopUrl, env.app), fallbackUrl: hopUrl };
}

// ---------------------------------------------------------------- 앱 안 이어가기

/** 앱 안에서 설치 페이지가 next 로 넘기기 전 채택을 기다리는 상한. 넘으면 기다리지 않고 간다. */
export const NATIVE_FORWARD_MAX_WAIT_MS = 1500;

/**
 * promise 를 최대 ms 만 기다린다. 거절되거나 시간이 지나면 fallback — **절대 거절하지 않고
 * 반드시 끝난다**(앱 안 이어가기가 멈춘 화면에 갇히지 않게).
 */
export function settleWithin<T>(promise: Promise<T>, fallback: T, ms: number): Promise<T> {
  return new Promise<T>((resolve) => {
    let done = false;
    const finish = (value: T) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(fallback), ms);
    promise.then(finish, () => finish(fallback));
  });
}

/** 앱 안 설치 페이지의 목적지 — next 가 홈이 아니면 next, 아니면 홈. */
export function nativeForwardPath(next: string | null | undefined, home: string): string {
  return next && !isHomePath(next) ? next : home;
}

// ---------------------------------------------------------------- Install Referrer

export interface ParsedInstallReferrer {
  clickId: string | null;
  next: string | null;
}

/**
 * Play Install Referrer 문자열 → { clickId, next }.
 * Play 는 referrer 파라미터를 한 번 디코드해 넘긴다("c=..&next=%2Fko%2F.."). 일부 경로는
 * 통째로 인코딩된 채("c%3D..%26next%3D..") 오므로 '=' 가 없으면 한 번 더 푼다.
 * 크루와 무관한 값(utm_source=google-play&utm_medium=organic 등)은 둘 다 null.
 */
export function parseInstallReferrer(raw: string | null | undefined): ParsedInstallReferrer {
  const empty = { clickId: null, next: null };
  if (!raw || typeof raw !== "string") return empty;
  let value = raw.trim();
  if (!value.includes("=") && /%3D/i.test(value)) {
    try {
      value = decodeURIComponent(value);
    } catch {
      return empty;
    }
  }
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(value);
  } catch {
    return empty;
  }
  const c = params.get("c");
  return {
    clickId: isCrewClickId(c) ? c.toLowerCase() : null,
    next: sanitizeCrewNextPath(params.get("next")),
  };
}

// ---------------------------------------------------------------- 로그인 핸드오프

/** 앱이 만든 nonce 형태(base64url 20..128자). DB CHECK(길이 20..128)와 같다. */
export function isValidHandoffNonce(value: unknown): value is string {
  return typeof value === "string" && /^[A-Za-z0-9_-]{20,128}$/.test(value);
}

/** 핸드오프 라우트가 이어 보내도 되는 우리 쪽 로그인 시작 경로. */
export const HANDOFF_SELF_CONTINUE_PATHS = [
  "/api/auth/naver/start",
  "/api/auth/line/start",
] as const;

/**
 * /api/crew/auth-handoff 의 continue 허용 목록(오픈 리다이렉트 차단).
 * - Supabase 프로젝트 인증 호스트의 /auth/v1/authorize
 * - 우리 출처의 Naver/LINE 시작 라우트
 * 사용자 정보(user:pass@)가 실린 URL 은 거절한다.
 */
export function isAllowedHandoffContinue(
  continueUrl: string | null | undefined,
  allow: { supabaseUrl?: string | null; selfOrigin: string },
): boolean {
  if (!continueUrl) return false;
  let url: URL;
  try {
    url = new URL(continueUrl);
  } catch {
    return false;
  }
  if (url.username || url.password) return false;
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;

  if (allow.supabaseUrl) {
    try {
      const supabase = new URL(allow.supabaseUrl);
      if (
        url.origin === supabase.origin &&
        url.protocol === "https:" &&
        url.pathname === "/auth/v1/authorize"
      ) {
        return true;
      }
    } catch {
      // 잘못된 env 는 Supabase 경로만 닫는다.
    }
  }

  let self: URL;
  try {
    self = new URL(allow.selfOrigin);
  } catch {
    return false;
  }
  return (
    url.origin === self.origin &&
    (HANDOFF_SELF_CONTINUE_PATHS as readonly string[]).includes(url.pathname)
  );
}
